import React, { useState, useEffect, useCallback } from 'react';
import { ThemeProvider, useTheme, Topbar, LoadingScreen, EmptyState } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

// ── Config ────────────────────────────────────────────────────────────────────
const ROLE_META = {
  student:          { color: COLORS.blue,   icon: '🎒', label: 'Student' },
  faculty:          { color: COLORS.amber,  icon: '👨‍🏫', label: 'Faculty' },
  admin:            { color: COLORS.purple, icon: '🛡️',  label: 'Admin' },
  authority:        { color: COLORS.green,  icon: '⭐',  label: 'Authority' },
  higher_authority: { color: '#f472b6',     icon: '👑',  label: 'Higher Authority' },
};

const TABS = [
  { key: 'all',              label: 'All',             icon: '👥' },
  { key: 'student',          label: 'Students',        icon: '🎒' },
  { key: 'faculty',          label: 'Faculty',         icon: '👨‍🏫' },
  { key: 'admin',            label: 'Admins',          icon: '🛡️' },
  { key: 'authority',        label: 'Authorities',     icon: '⭐' },
];

// ── Profile modal ─────────────────────────────────────────────────────────────
function ProfileModal({ user, onClose, s }) {
  const meta = ROLE_META[user.role] || ROLE_META.student;

  const rows = [
    { label: 'Email',      value: user.email },
    { label: 'Role',       value: meta.label + (user.admin_type ? ` (${user.admin_type})` : '') + (user.authority_type ? ` (${user.authority_type})` : '') },
    { label: 'Department', value: user.department || '—' },
    { label: 'Joined',     value: user.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—' },
  ];

  // Role-specific stats
  const stats = [];
  if (user.role === 'admin') {
    stats.push(
      { label: 'Verified',  value: user.verified_count ?? 0, color: COLORS.blue },
      { label: 'Rejected',  value: user.rejected_count ?? 0, color: '#f87171' },
      { label: 'Negligence', value: user.negligence_count ?? 0, color: COLORS.amber },
    );
  } else if (user.role === 'authority') {
    stats.push(
      { label: 'Resolved',  value: user.resolved_count ?? 0, color: COLORS.green },
      { label: 'Assigned',  value: user.assigned_count ?? 0, color: COLORS.cyan },
      { label: 'Negligence', value: user.negligence_count ?? 0, color: COLORS.amber },
    );
  } else {
    stats.push(
      { label: 'Complaints', value: user.complaint_count ?? 0, color: COLORS.purple },
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={onClose}>
      <div style={{ ...s.card, width: '100%', maxWidth: 440, padding: 0, overflow: 'hidden', animation: 'scaleIn 0.2s ease-out' }}
        onClick={e => e.stopPropagation()}>

        {/* Header band */}
        <div style={{ padding: '28px 28px 20px', background: `linear-gradient(135deg, ${meta.color}22, ${meta.color}08)`, borderBottom: `1px solid ${meta.color}33`, position: 'relative' }}>
          <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, color: '#fff', cursor: 'pointer', width: 30, height: 30, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: `linear-gradient(135deg, ${meta.color}55, ${meta.color}22)`, border: `2px solid ${meta.color}66`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 900, color: '#fff', flexShrink: 0 }}>
              {user.name?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>{user.name}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 3 }}>{user.email}</div>
              <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: `${meta.color}33`, color: meta.color, border: `1px solid ${meta.color}55` }}>
                  {meta.icon} {meta.label.toUpperCase()}
                </span>
                {user.admin_type && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.2)' }}>
                    {user.admin_type.toUpperCase()}
                  </span>
                )}
                {user.authority_type && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.2)' }}>
                    {user.authority_type.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats row */}
        {stats.length > 0 && (
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            {stats.map(st => (
              <div key={st.label} style={{ flex: 1, padding: '14px 0', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.07)' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: st.color }}>{st.value}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>{st.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Detail rows */}
        <div style={{ padding: '18px 28px 24px' }}>
          {rows.map(({ label, value }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
              <span style={{ fontSize: 13, color: '#fff', fontWeight: 600, maxWidth: 240, textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
            </div>
          ))}

          {user.negligence_count > 0 && (
            <div style={{ marginTop: 14, padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', fontSize: 12, color: '#fca5a5', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>⚠</span> {user.negligence_count} negligence flag{user.negligence_count !== 1 ? 's' : ''} recorded
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── User card (grid item) ─────────────────────────────────────────────────────
function UserCard({ user, index, onClick }) {
  const meta = ROLE_META[user.role] || ROLE_META.student;

  const statLine = () => {
    if (user.role === 'admin')
      return `${user.verified_count ?? 0} verified · ${user.rejected_count ?? 0} rejected`;
    if (user.role === 'authority')
      return `${user.resolved_count ?? 0} resolved · ${user.assigned_count ?? 0} assigned`;
    return `${user.complaint_count ?? 0} complaint${(user.complaint_count ?? 0) !== 1 ? 's' : ''}`;
  };

  return (
    <div className="ur-card-hover" onClick={onClick}
      style={{
        padding: '16px 18px', borderRadius: 14, cursor: 'pointer',
        animation: 'fadeUp 0.35s ease-out both', animationDelay: `${index * 0.025}s`,
        background: `linear-gradient(135deg, ${meta.color}12, ${meta.color}05)`,
        border: `1px solid ${meta.color}33`,
        display: 'flex', flexDirection: 'column', gap: 10, overflow: 'hidden',
      }}>
      {/* Avatar + name row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 12, flexShrink: 0,
          background: `linear-gradient(135deg, ${meta.color}44, ${meta.color}22)`,
          border: `2px solid ${meta.color}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, fontWeight: 900, color: '#fff',
        }}>
          {user.name?.[0]?.toUpperCase() || '?'}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.name}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.email}
          </div>
        </div>
      </div>

      {/* Role badge — on its own line, contained */}
      <div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
          background: `${meta.color}28`, color: meta.color,
          border: `1px solid ${meta.color}44`, whiteSpace: 'nowrap',
        }}>
          {meta.icon} {meta.label.toUpperCase()}
          {user.admin_type && <span style={{ opacity: 0.7 }}> · {user.admin_type}</span>}
          {user.authority_type && <span style={{ opacity: 0.7 }}> · {user.authority_type}</span>}
        </span>
      </div>

      {/* Dept + stats row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user.department || (user.role === 'student' || user.role === 'faculty' ? 'No dept' : '—')}
        </div>
        <div style={{ fontSize: 11, color: meta.color, fontWeight: 600, flexShrink: 0 }}>{statLine()}</div>
      </div>

      {user.negligence_count > 0 && (
        <div style={{ fontSize: 10, color: '#fca5a5', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6, padding: '3px 8px', alignSelf: 'flex-start' }}>
          ⚠ {user.negligence_count} negligence
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
function AllUsersInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/higher-authority/users');
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const counts = TABS.reduce((acc, t) => {
    acc[t.key] = t.key === 'all' ? users.length : users.filter(u => u.role === t.key).length;
    return acc;
  }, {});

  const filtered = users.filter(u => {
    const matchTab = activeTab === 'all' || u.role === activeTab;
    const q = search.toLowerCase();
    const matchSearch = !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.department || '').toLowerCase().includes(q);
    return matchTab && matchSearch;
  });

  if (loading) return <LoadingScreen />;

  return (
    <div style={s.page}>
      <div style={s.bgGrid} /><div style={s.bgOrb1} /><div style={s.bgOrb2} /><div style={s.bgOrb3} />
      <div style={s.layout}>
        <Sidebar collapsed={!sidebarOpen} onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar title="All Users" subtitle="University-wide user directory"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <button className="ur-btn-hover" style={{ ...s.btnSecondary, fontSize: 12, padding: '6px 14px' }} onClick={fetchUsers}>
                Refresh
              </button>
            }
          />
          <div style={s.content}>
            {error && <div style={{ ...s.alertErr, marginBottom: 16 }}>{error}</div>}

            {/* Summary stat row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 24 }}>
              {TABS.filter(t => t.key !== 'all').map(t => {
                const meta = ROLE_META[t.key];
                return (
                  <div key={t.key} style={{ ...s.card, padding: '14px 16px', border: `1px solid ${meta.color}33`, background: `${meta.color}0a`, textAlign: 'center', cursor: 'pointer' }}
                    onClick={() => setActiveTab(t.key)}>
                    <div style={{ fontSize: 22, marginBottom: 4 }}>{t.icon}</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: meta.color }}>{counts[t.key]}</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{t.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Tabs + search */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {TABS.map(t => {
                  const meta = ROLE_META[t.key] || { color: COLORS.purple };
                  const active = activeTab === t.key;
                  return (
                    <button key={t.key} onClick={() => setActiveTab(t.key)} style={{ padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600, transition: 'all 0.2s', border: `1px solid ${active ? meta.color : 'rgba(255,255,255,0.15)'}`, background: active ? `${meta.color}33` : 'rgba(255,255,255,0.05)', color: active ? meta.color : 'rgba(255,255,255,0.6)' }}>
                      {t.icon} {t.label} ({counts[t.key] || 0})
                    </button>
                  );
                })}
              </div>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search name, email, department..."
                style={{ marginLeft: 'auto', padding: '8px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: 13, outline: 'none', minWidth: 220 }}
              />
            </div>

            {/* Results count */}
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 14 }}>
              Showing {filtered.length} user{filtered.length !== 1 ? 's' : ''}
              {search && ` matching "${search}"`}
            </div>

            {/* Grid */}
            {!filtered.length
              ? <EmptyState text="No users found" sub="Try adjusting your search or filter" />
              : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
                  {filtered.map((u, i) => (
                    <UserCard key={u.id} user={u} index={i} onClick={() => setSelected(u)} />
                  ))}
                </div>
              )
            }
          </div>
        </div>
      </div>

      {selected && <ProfileModal user={selected} onClose={() => setSelected(null)} s={s} />}
    </div>
  );
}

export default function AllUsers() {
  return <ThemeProvider><AllUsersInner /></ThemeProvider>;
}
